import i18n from 'i18n';
import { getCurrentSellerId, getSellerNamespace } from './sellerTrans';

export const t_old = (text: string, options?: object) => i18n.t(text, options);

/**
 * Translation function with automatic seller-specific namespace support.
 * Automatically checks for seller-specific translations and falls back to default.
 *
 * @param text - Translation key(s)
 * @param options - i18next translation options
 *
 * IMPORTANT USAGE NOTES:
 *
 * 1. Runtime Usage (Recommended):
 *    Use directly in React components where it evaluates at render time:
 *    ```tsx
 *    function MyComponent() {
 *      return <div>{t('MyKey')}</div>  // Correct
 *    }
 *    ```
 *
 * 2. Module-Level Usage (Constants):
 *    DO NOT use at module initialization time (top-level of files).
 *    The seller context is not available yet, so seller-specific translations won't work.
 *
 *    WRONG:
 *    ```ts
 *    const OPTIONS = [{ label: t('MyKey') }];  // Evaluated at import time
 *    export default { OPTIONS };
 *    ```
 *
 *    CORRECT - Use getter functions for lazy evaluation:
 *    ```ts
 *    const getOptions = () => [{ label: t('MyKey') }];
 *    export default {
 *      get OPTIONS() { return getOptions(); }  // Evaluated when accessed
 *    };
 *    ```
 *
 * See src/constants/stockCount.ts for a complete example of the getter pattern.
 */
export const t = (text: string | string[], options?: object) => {
  const sellerId = getCurrentSellerId();
  const sellerNamespace = getSellerNamespace(sellerId);

  // Try seller namespace first if available
  if (sellerNamespace) {
    const sellerTranslation = i18n.t(text, {
      ns: sellerNamespace,
      ...options,
      defaultValue: null,
    });

    // If translation found in seller namespace (not null and not the key itself)
    if (sellerTranslation !== null && sellerTranslation !== text) {
      return sellerTranslation;
    }
  }

  // Fallback to default translation
  return i18n.t(text, options);
};

/**
 * Lazy translation function for module-level constants (routes, options, etc.)
 * Returns a function that evaluates the translation at runtime.
 *
 * Use this for module-level constant definitions where t() would be called at import time:
 *
 * WRONG:
 * ```ts
 * const routes = [{ name: t('MyKey') }];  // Evaluated at import time
 * ```
 *
 * CORRECT:
 * ```ts
 * import { tLazy } from 'helpers/i18n';
 * const routes = [{ name: tLazy('MyKey') }];  // Returns function, evaluated at runtime
 * ```
 *
 * @param text - Translation key(s)
 * @param options - i18next translation options
 * @returns Function that returns the translated string when called
 */
export const tLazy = (text: string | string[], options?: object) => {
  return () => t(text, options);
};

/**
 * Helper to get the name value, handling both string and lazy function formats
 * @param name - Either a string or a lazy translation function
 * @returns The evaluated name string
 */
export const getLazyName = (
  name: string | (() => string) | undefined
): string => {
  if (typeof name === 'function') {
    return name();
  }
  return name || '';
};
